import type * as React from "react";
import { Link } from "react-router-dom";
import { BsFillPassportFill, BsPeopleFill } from "react-icons/bs";
import {
  HiCalendar,
  HiHome,
  HiIdentification,
  HiInbox,
  HiMiniBuildingOffice,
} from "react-icons/hi2";
import { IoLayers } from "react-icons/io5";
import { MdHelp } from "react-icons/md";
import { RiSendPlaneFill } from "react-icons/ri";

import { Logo } from "@ospra/design-system/components/logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarSeparator,
} from "@ospra/design-system/components/sidebar";

import { useUnreadCount } from "@/features/Inbox/service/query";
import { useAuth } from "@/hooks/useAuth";

import { NavItems } from "./navItems";
import { NavUser } from "./navUser";
import { ThemeToggle } from "./ThemeToggle";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.data.unreadCount ?? 0;

  const data = {
    navHead: [
      {
        name: "Inbox",
        url: "/inbox",
        icon: HiInbox,
        badge: unreadCount > 0 ? String(unreadCount) : undefined,
      },
    ],
    navMain: [
      {
        name: "Overview",
        url: "/overview",
        icon: HiHome,
      },
      {
        name: "Battery Passports",
        url: "/battery-passports",
        icon: BsFillPassportFill,
      },
      {
        name: "Battery Models",
        url: "/battery-models",
        icon: IoLayers,
      },
      {
        name: "Events",
        url: "/events",
        icon: HiCalendar,
      },
      {
        name: "Partners",
        url: "/partners",
        icon: BsPeopleFill,
      },
      {
        name: "Access Requests",
        url: "/access-requests",
        icon: HiIdentification,
      },
      {
        name: "Organization",
        url: "/organization",
        icon: HiMiniBuildingOffice,
        roles: ["admin"] as const,
        items: [
          {
            name: "Members",
            url: "/organization/members",
            icon: BsPeopleFill,
          },
        ],
      },
    ],
    navTail: [
      {
        name: "Support",
        url: "#",
        icon: MdHelp,
      },
      {
        name: "Feedback",
        url: "#",
        icon: RiSendPlaneFill,
      },
    ],
  };

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="px-1.5 pt-2">
            <Link to="/overview">
              <Logo className="h-6 w-auto" />
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavItems items={data.navHead} />
        <SidebarSeparator className="mx-0" />
        <NavItems itemButtonClassName="h-9.5" items={data.navMain} />
        <NavItems
          className="mt-auto"
          itemButtonClassName="h-8 text-xs"
          items={data.navTail}
        />
      </SidebarContent>
      <SidebarSeparator className="mx-0 my-2" />
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <ThemeToggle />
          </SidebarMenuItem>
        </SidebarMenu>
        {user ? <NavUser user={user} /> : null}
      </SidebarFooter>

    </Sidebar>
  );
}
